/* ============================================================
 * RVRN Finder — Section JS
 *
 * 3-step wheel configurator: Vehicle → Direction → Finish.
 * Queries public /search?view=finder endpoint — no API token needed.
 *
 * Dependencies:
 *   - rvrn-product-card template (data attributes)
 *   - rvrn.vehicle_config / rvrn.design_directions / rvrn.finishes metafields
 * ============================================================ */

(function () {
  'use strict';

  const MIN_RESULTS = 4;
  const PAGE_BATCH = 12;
  const MAX_COLLECT_PAGES = 6;
  const GRID_STORAGE_KEY = 'rvrn-product-grid-columns';
  const MOBILE_GRID_STORAGE_KEY = 'rvrn-product-grid-columns-mobile';

  // --- Section bootstrap ---
  document.addEventListener('DOMContentLoaded', function () {
    const sections = document.querySelectorAll('.rvrn-finder');
    sections.forEach(function (el) {
      if (el.dataset.initialized) return;
      el.dataset.initialized = '1';
      try { new RVRNFinder(el); } catch (e) { console.error('RVRN Finder init failed:', e); }
    });
  });

  // ============================================================
  // Constructor
  // ============================================================
  function RVRNFinder(root) {
    this.root = root;
    this.sectionId = root.dataset.sectionId || root.id.replace('shopify-section-', '');

    // Config from data attributes (Liquid-injected)
    this.config = {
      step1Title: root.dataset.step1Title || 'Select your vehicle',
      step2Title: root.dataset.step2Title || 'Choose your direction',
      step3Title: root.dataset.step3Title || 'Choose your finish',
      step1Desc: root.dataset.step1Desc || '',
      step3Desc: root.dataset.step3Desc || '',
      manualBtn: root.dataset.manualBtn || "Can't find your vehicle? Enter it manually",
      primaryCta: root.dataset.primaryCta || 'Show My Options',
      specialistCta: root.dataset.specialistCta || 'Talk to a Specialist',
      specialistLink: root.dataset.specialistLink || 'mailto:sales@rvrnwheel.com',
      productsPerRow: parseInt(root.dataset.productsPerRow) || 4,
      hideOutOfStock: root.dataset.hideOutOfStock !== 'false',
      priceUnit: root.dataset.priceUnit || '/ wheel',
      showCompareAt: root.dataset.showCompareAt === 'true',
      collectionHandle: root.dataset.finderCollection || 'fully-forged-wheels-collection'
    };

    // Metafield data
    try {
      var vcRaw = root.querySelector('[data-finder-vehicle-config]');
      var vcText = vcRaw ? vcRaw.textContent : '{"vehicles":[]}';
      this.vehicleData = JSON.parse(vcText).vehicles || [];
    } catch (e) { console.warn('RVRN Finder: failed to parse vehicle_config JSON', e.message); this.vehicleData = []; }
    try {
      var ddRaw = root.querySelector('[data-finder-directions]');
      var ddText = ddRaw ? ddRaw.textContent : '{"directions":[]}';
      this.directions = JSON.parse(ddText).directions || [];
    } catch (e) { console.warn('RVRN Finder: failed to parse design_directions JSON', e.message); this.directions = []; }
    try {
      var fiRaw = root.querySelector('[data-finder-finishes]');
      var fiText = fiRaw ? fiRaw.textContent : '{"finishes":[]}';
      this.finishes = JSON.parse(fiText).finishes || [];
    } catch (e) { console.warn('RVRN Finder: failed to parse finishes JSON', e.message); this.finishes = []; }

    // DOM refs
    this.stepTitle = root.querySelector('.rvrn-finder__step-title');
    this.stepItems = root.querySelectorAll('.rvrn-finder__step-item');
    this.stage = root.querySelector('.rvrn-finder__stage');
    this.resultBar = root.querySelector('.rvrn-finder__result-bar');
    this.resultSummary = root.querySelector('.rvrn-finder__result-summary');
    // Results section is OUTSIDE Finder root — use document.getElementById
    this.results = document.getElementById('rvrn-finder-results-' + this.sectionId);
    this.productGrid = document.getElementById('rvrn-finder-product-grid-' + this.sectionId);
    this.collectionCount = document.getElementById('rvrn-finder-collection-count-' + this.sectionId);
    this.activeFilters = document.getElementById('rvrn-finder-active-filters-' + this.sectionId);
    this.loadMore = document.getElementById('rvrn-finder-load-more-' + this.sectionId);
    this.loadMoreBtn = this.loadMore ? this.loadMore.querySelector('.rvrn-finder__load-more-btn') : null;
    this.returnBtn = document.getElementById('rvrn-finder-collection-return-' + this.sectionId);
    this.showBtn = document.getElementById('rvrn-finder-show-btn-' + this.sectionId);

    // Card template config (for metafield namespace/key extraction in _executeSearch)
    var templateEl = document.getElementById('rvrn-product-card-template-' + this.sectionId);
    this.cardTemplate = templateEl ? templateEl.innerHTML : null;
    var configEl = document.getElementById('rvrn-product-card-config-' + this.sectionId);
    try {
      this.cardConfig = JSON.parse(configEl.textContent || '{}');
    } catch (e) { this.cardConfig = {}; }

    // State
    this.state = { step: 1, vehicleMode: 'standard', year: '', make: '', model: '', style: '', finish: '', showMoreFinishes: false, filterLevel: '', vehicleTag: null };
    this.selectedFinishes = [];
    this.resultsData = { products: [], degradation: null, totalCount: 0, currentLevelTags: [], currentPage: 1, hasNextPage: false };
    this._loadMoreInFlight = false;
    this._loadMoreMode = 'more';
    this.productColumns = this.config.productsPerRow;
    this.mobileProductColumns = 1;

    // Restore saved density
    try {
      var saved = localStorage.getItem(GRID_STORAGE_KEY);
      if (saved === '4' || saved === '5') this.productColumns = parseInt(saved);
      var savedM = localStorage.getItem(MOBILE_GRID_STORAGE_KEY);
      if (savedM === '1' || savedM === '2') this.mobileProductColumns = parseInt(savedM);
    } catch (e) {}

    // Restore from URL
    this._restoreFromURL();

    // Advance step to match restored selections so the UI is in sync
    if (this.state.finish) {
      this.state.step = 3;
    } else if (this.state.style) {
      this.state.step = 2;
    }

    // Bind events
    this._bindEvents();

    // Render initial step
    this._renderStep();
    this._updateProgress();
    this._updateResultBar();
    this._applyDensity();

    // Auto-search when the URL carries restored selections
    if (this.state.year || this.state.style || this.state.finish) {
      this._triggerSearch();
    }

    root.classList.add('motion-ready');
  }

  // ============================================================
  // URL state
  // ============================================================
  RVRNFinder.prototype._restoreFromURL = function () {
    var params = new URLSearchParams(window.location.search);
    var vehicle = params.get('vehicle');
    if (vehicle) {
      var parts = vehicle.split(' ');
      if (parts.length >= 3) {
        this.state.year = parts[0];
        this.state.make = parts.slice(1, -1).join(' ');
        this.state.model = parts[parts.length - 1];
        this._resolveVehicleTag();
      }
    }
    var style = params.get('style');
    if (style) {
      if (style === 'Not Sure Yet') {
        this.state.style = 'Not Sure Yet';
      } else {
        var dir = this.directions.find(function (d) { return d.label === style; });
        if (dir) { this.state.style = dir.label; }
      }
    }
    var finish = params.get('finish');
    if (finish) {
      if (finish === 'Not Sure Yet' || finish === 'Custom Finish') {
        this.state.finish = finish;
      } else {
        var fin = this.finishes.find(function (f) { return f.label === finish; });
        if (fin) { this.state.finish = fin.label; }
      }
    }
  };

  RVRNFinder.prototype._pushURL = function () {
    var params = new URLSearchParams();
    if (this.state.year && this.state.make && this.state.model) {
      var vehicle = this.state.year + ' ' + this.state.make + ' ' + this.state.model;
      params.set('vehicle', vehicle);
      // Broadcast vehicle update: customer-vehicle.js persists to localStorage
      // (14-day rolling expiry) and auto-fills Year/Make/Model form fields.
      try {
        document.dispatchEvent(new CustomEvent('rvrn:vehicle-updated', { detail: { value: vehicle } }));
      } catch (e) {}
    }
    if (this.state.style) params.set('style', this.state.style);
    if (this.state.finish) params.set('finish', this.state.finish);
    var qs = params.toString();
    var newUrl = window.location.pathname + (qs ? '?' + qs : '');
    window.history.replaceState({}, '', newUrl);
  };

  // ============================================================
  // Vehicle config helpers
  // ============================================================
  RVRNFinder.prototype._availableYears = function () {
    var years = new Set();
    this.vehicleData.forEach(function (v) {
      for (var y = v.start_year; y <= v.end_year; y++) years.add(y);
    });
    return Array.from(years).sort(function (a, b) { return b - a; });
  };

  RVRNFinder.prototype._availableMakes = function (year) {
    if (!year) return [];
    var yr = parseInt(year);
    var makes = new Set();
    this.vehicleData.forEach(function (v) {
      if (yr >= v.start_year && yr <= v.end_year) makes.add(v.make);
    });
    return Array.from(makes).sort();
  };

  RVRNFinder.prototype._availableModels = function (year, make) {
    if (!year || !make) return [];
    var yr = parseInt(year);
    var models = new Set();
    this.vehicleData.forEach(function (v) {
      if (yr >= v.start_year && yr <= v.end_year && v.make.toLowerCase() === make.toLowerCase()) {
        models.add(v.model);
      }
    });
    return Array.from(models).sort();
  };

  RVRNFinder.prototype._resolveVehicleTag = function () {
    if (!this.state.year || !this.state.make || !this.state.model) return;
    var yr = parseInt(this.state.year);
    var match = this.vehicleData.find(function (v) {
      return yr >= v.start_year && yr <= v.end_year &&
             v.make.toLowerCase() === this.state.make.toLowerCase() &&
             v.model.toLowerCase() === this.state.model.toLowerCase();
    }, this);
    if (match) {
      this.state.filterLevel = match.filter_level;
      this.state.vehicleTag = match.product_tag || null;
      if (match.collection_handle) this.state.collectionHandle = match.collection_handle;
    } else {
      this.state.filterLevel = '';
      this.state.vehicleTag = null;
    }
  };

  // ============================================================
  // Step rendering
  // ============================================================
  RVRNFinder.prototype._renderStep = function () {
    var s = this.state;
    if (this.stepTitle) this.stepTitle.textContent = this.config['step' + s.step + 'Title'];
    var html = '';
    switch (s.step) {
      case 1: html = this._renderVehicleStep(); break;
      case 2: html = this._renderDirectionStep(); break;
      case 3: html = this._renderFinishStep(); break;
    }
    if (this.stage) this.stage.innerHTML = html;
    this._updateResultBar();
  };

  RVRNFinder.prototype._renderVehicleStep = function () {
    var s = this.state;
    var modeTabs = '<div class="rvrn-finder__vehicle-mode">' +
      '<button class="rvrn-finder__mode-btn' + (s.vehicleMode === 'standard' ? ' active' : '') + '" data-action="switchMode" data-mode="standard">Select from Vehicle List</button>' +
      '<button class="rvrn-finder__mode-btn' + (s.vehicleMode === 'classic' ? ' active' : '') + '" data-action="switchMode" data-mode="classic">Classic / Special Vehicle</button>' +
      '</div>';

    if (s.vehicleMode === 'standard') {
      var years = this._availableYears();
      var makes = this._availableMakes(s.year);
      var models = this._availableModels(s.year, s.make);
      var fields = '<div class="rvrn-finder__vehicle-fields">' +
        this._selectField('Year', years, s.year, 'year') +
        this._selectField('Make', makes, s.make, 'make') +
        this._selectField('Model', models, s.model, 'model') +
        '<button class="rvrn-finder__continue-btn" data-action="continueVehicle">Continue →</button>' +
        '</div>';
      var note = this.config.step1Desc ? '<p class="rvrn-finder__stage-note">' + this._escapeHtml(this.config.step1Desc) + '</p>' : '';
      var manual = '<div class="rvrn-finder__vehicle-note-row">' + note +
        '<button class="rvrn-finder__manual-link" data-action="switchMode" data-mode="classic">' + this._escapeHtml(this.config.manualBtn) + ' →</button>' +
        '</div>';
      return modeTabs + fields + manual;
    } else {
      var classic = '<div class="rvrn-finder__classic-fields">' +
        '<input class="rvrn-finder__text-field" type="text" placeholder="Year (e.g. 1967)" data-field="year" value="' + this._escapeAttr(s.year || '') + '">' +
        '<input class="rvrn-finder__text-field" type="text" placeholder="Make (e.g. Chevrolet)" data-field="make" value="' + this._escapeAttr(s.make || '') + '">' +
        '<input class="rvrn-finder__text-field" type="text" placeholder="Model (e.g. Camaro)" data-field="model" value="' + this._escapeAttr(s.model || '') + '">' +
        '<button class="rvrn-finder__continue-btn" data-action="continueVehicle">Continue →</button>' +
        '</div>';
      var classicNote = '<p class="rvrn-finder__stage-note">Enter only the year, make, and model. Rare, modified, continuation, and coachbuilt vehicles are welcome; vehicle data and final fitment will be verified by RVRN Engineering.</p>';
      return modeTabs + classic + classicNote;
    }
  };

  RVRNFinder.prototype._renderDirectionStep = function () {
    var s = this.state;
    // Combine metafield directions + hardcoded "Not Sure Yet"
    var items = this.directions.map(function (d) {
      return '<button class="rvrn-finder__choice' + (s.style === d.label ? ' selected' : '') + '" data-action="selectDirection" data-value="' + this._escapeAttr(d.label) + '">' + this._escapeHtml(d.label) + '</button>';
    }, this).join('');
    items += '<button class="rvrn-finder__choice' + (s.style === 'Not Sure Yet' ? ' selected' : '') + '" data-action="selectDirection" data-value="Not Sure Yet">Not Sure Yet</button>';
    var grid = '<div class="rvrn-finder__choice-grid">' + items + '</div>';
    var footer = '<div class="rvrn-finder__stage-footer">' +
      (s.style ? '<button class="rvrn-finder__stage-back" data-action="backToStep" data-step="1">← Back to Vehicle</button>' : '<span></span>') +
      '</div>';
    return grid + footer;
  };

  RVRNFinder.prototype._renderFinishStep = function () {
    var s = this.state;
    var primary = this.finishes.filter(function (f) { return f.primary; });
    var secondary = this.finishes.filter(function (f) { return !f.primary; });

    function finishBtn(f) {
      return '<button class="rvrn-finder__choice' + (s.finish === f.label ? ' selected' : '') + '" data-action="selectFinish" data-value="' + this._escapeAttr(f.label) + '">' +
        '<span class="rvrn-finder__finish-choice"><span class="rvrn-finder__finish-dot" style="--finish-color:' + f.color + '"></span>' + this._escapeHtml(f.label) + '</span></button>';
    }

    var items = primary.map(finishBtn, this).join('');

    if (s.showMoreFinishes) {
      items += secondary.map(finishBtn, this).join('');
      items += finishBtn.call(this, { label: 'Custom Finish', color: '#d00a07' });
      items += finishBtn.call(this, { label: 'Not Sure Yet', color: '#d7d7d7' });
    }

    var grid = '<div class="rvrn-finder__choice-grid rvrn-finder__choice-grid--finish">' + items + '</div>';

    var moreBtn = '<button class="rvrn-finder__finish-more" data-action="' + (s.showMoreFinishes ? 'showFewerFinishes' : 'showMoreFinishes') + '">' + (s.showMoreFinishes ? 'Show fewer finishes −' : 'View more finishes +') + '</button>';

    var desc = this.config.step3Desc ? '<p class="rvrn-finder__stage-note">' + this._escapeHtml(this.config.step3Desc) + '</p>' : '';
    var footer = '<div class="rvrn-finder__stage-footer">' +
      '<button class="rvrn-finder__stage-back" data-action="backToStep" data-step="2">← Back to Direction</button>' + desc + '</div>';

    return grid + moreBtn + footer;
  };

  // ============================================================
  // Result bar
  // ============================================================
  RVRNFinder.prototype._updateResultBar = function () {
    var s = this.state;
    if (!this.resultBar || !this.resultSummary) return;
    var hasVehicle = s.year && s.make && s.model;
    var hasStyle = !!s.style;
    var hasFinish = !!s.finish;

    if (hasVehicle || hasStyle || hasFinish) {
      this.resultBar.classList.add('visible');
      var parts = [];
      if (hasVehicle) parts.push(s.year + ' ' + s.make + ' ' + s.model);
      if (hasStyle) parts.push(s.style);
      if (hasFinish) parts.push(s.finish);
      this.resultSummary.innerHTML = parts.join(' <span>·</span> ');
    } else {
      this.resultBar.classList.remove('visible');
    }
  };

  // ============================================================
  // Query & degradation
  // ============================================================
  RVRNFinder.prototype._buildDegradationPlan = function () {
    var s = this.state;
    var V = s.vehicleTag;
    var D = this._getStyleTag(s.style);
    var F = this._getFinishTag(s.finish);

    var chain = [];

    if (V && D && F) {
      chain = [
        { tags: [V, D, F] },
        { tags: [V, D] },
        { tags: [D, F] },
        { tags: [D] }
      ];
    } else if (V && D) {
      chain = [
        { tags: [V, D] },
        { tags: [D] }
      ];
    } else if (D && F) {
      chain = [
        { tags: [D, F] },
        { tags: [D] }
      ];
    } else if (V && F) {
      chain = [
        { tags: [V, F] },
        { tags: [V] },
        { tags: [F] }
      ];
    } else if (V) {
      chain = [{ tags: [V] }];
    } else if (D) {
      chain = [{ tags: [D] }];
    } else if (F) {
      chain = [{ tags: [F] }];
    }

    // Always append All as final fallback
    chain.push({ tags: [] });

    return chain.map(function (step, i) {
      return { level: i + 1, tags: step.tags };
    });
  };

  RVRNFinder.prototype._getStyleTag = function (label) {
    // "Not Sure Yet" is a hardcoded UI option — never produces a tag
    if (label === 'Not Sure Yet') return null;
    var dir = this.directions.find(function (d) { return d.label === label; });
    return dir ? dir.tag : null;
  };

  RVRNFinder.prototype._getFinishTag = function (label) {
    // Hardcoded UI options — never produce a tag
    if (label === 'Not Sure Yet' || label === 'Custom Finish') return null;
    var fin = this.finishes.find(function (f) { return f.label === label; });
    return fin ? fin.tag : null;
  };

  RVRNFinder.prototype._handleize = function (str) {
    return String(str || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  };

  RVRNFinder.prototype._searchQuery = function (tags) {
    // URL path-style tag filtering on the data-source collection:
    // empty = collection root (fallback tier), otherwise handleized tags
    // joined by '+' (Shopify applies server-side AND semantics).
    if (!tags || tags.length === 0) return '';
    var self = this;
    return tags.map(function (t) { return self._handleize(t); }).join('+');
  };

  RVRNFinder.prototype._executeSearch = function (tags, page) {
    var self = this;
    var path = this._searchQuery(tags);
    var pageQs = page > 1 ? '&page=' + page : '';
    var url = (window.router || '') + '/collections/' + encodeURIComponent(this.config.collectionHandle)
      + (path ? '/' + path : '')
      + '?view=finder&sort_by=best-selling' + pageQs;

    return fetch(url).then(function (r) { return r.json(); })
      .then(function (data) {
        if (data && data.results) {
          // Filter OOS if needed
          var products = data.results;
          if (self.config.hideOutOfStock) {
            products = products.filter(function (p) { return p.available; });
          }
          return {
            products: products,
            totalCount: data.total,
            hasNextPage: !!data.hasNextPage,
            page: data.page || 1
          };
        }
        return { products: [], totalCount: 0, hasNextPage: false, page: 1 };
      });
  };

  RVRNFinder.prototype._runDegradation = function () {
    var self = this;
    var plan = this._buildDegradationPlan();
    var seen = new Set();
    var allProducts = [];
    var degradationLevel = null;
    var lastTags = [];
    var lastPage = 1;
    var lastHasNext = false;

    // Collect a tier's available matches across pages so results are complete
    // and degradation is decided on the full tier count. Bounded by
    // MAX_COLLECT_PAGES so a huge tier (hundreds of products) cannot stall the
    // UI with a long serial fetch chain — anything beyond the cap is reached
    // through Load More. The fallback tier (no tags) keeps pagination.
    function collectLevel(level, page) {
      var collected = [];
      var hasMore = false;
      var stopPage = page;
      var emptyStreak = 0;
      function nextPage(p) {
        return self._executeSearch(level.tags, p).then(function (result) {
          collected = collected.concat(result.products);
          stopPage = p;
          if (result.products.length === 0) {
            emptyStreak++;
          } else {
            emptyStreak = 0;
          }
          // Two consecutive OOS-only pages mean no more available products:
          // end the tier so the button goes straight to Show Less instead of
          // a dead "Show More" click.
          hasMore = result.hasNextPage && emptyStreak < 2;
          if (level.tags.length === 0) return collected;
          if (result.hasNextPage && p < MAX_COLLECT_PAGES && emptyStreak < 2) return nextPage(p + 1);
          return collected;
        });
      }
      return nextPage(page).then(function (prods) {
        return { products: prods, hasNextPage: hasMore, page: stopPage };
      });
    }

    function tryLevel(index) {
      if (index >= plan.length) {
        self.resultsData = { products: allProducts, degradation: degradationLevel, totalCount: allProducts.length, currentLevelTags: lastTags, currentPage: lastPage, hasNextPage: lastHasNext };
        return Promise.resolve();
      }
      var level = plan[index];
      return collectLevel(level, 1).then(function (res) {
        res.products.forEach(function (p) {
          if (!seen.has(p.id)) {
            seen.add(p.id);
            p._level = level.level;
            allProducts.push(p);
          }
        });
        lastTags = level.tags;
        lastPage = res.page;
        lastHasNext = res.hasNextPage;

        if (index === plan.length - 1 || allProducts.length >= MIN_RESULTS) {
          self.resultsData = { products: allProducts, degradation: degradationLevel, totalCount: allProducts.length, currentLevelTags: lastTags, currentPage: lastPage, hasNextPage: lastHasNext };
          return;
        }

        if (!degradationLevel) degradationLevel = level.level;
        return tryLevel(index + 1);
      });
    }

    return tryLevel(0);
  };

  // ============================================================
  // Product card rendering
  // ============================================================
  RVRNFinder.prototype._renderProductCard = function (product) {
    var image = product.image || '';
    var alt = product.image_alt || product.title || '';
    var series = product.series || '';
    var title = product.title || '';
    var name = product.model_name || title.split(':')[0].trim() || '';
    var priceStr = product.price || '';
    var unit = this.config.priceUnit;

    var html = '<a class="rvrn-product-card__click" href="/products/' + this._escapeAttr(product.handle) + '">';
    html += '<div class="rvrn-product-card__media">';
    html += '<img class="rvrn-product-card__image" src="' + this._escapeAttr(image) + '" alt="' + this._escapeAttr(alt) + '" loading="lazy" onload="this.classList.add(\'is-loaded\')" />';
    html += '</div>';
    html += '<div class="rvrn-product-card__info">';
    html += '<div class="rvrn-product-card__meta">';
    html += '<span class="rvrn-product-card__series">' + this._escapeHtml(series) + '</span>';
    html += '</div>';
    html += '<span class="rvrn-product-card__name">' + this._escapeHtml(name) + '</span>';
    html += '<div class="rvrn-product-card__price">';
    html += '<span class="rvrn-product-card__price-from">STARTING AT</span>';
    html += '<span class="rvrn-product-card__price-amount">' + this._escapeHtml(priceStr) + '</span>';
    html += '<span class="rvrn-product-card__price-unit">' + this._escapeHtml(unit) + '</span>';
    html += '</div>';
    html += '</div>';
    html += '</a>';
    return html;
  };

  // ============================================================
  // Results rendering
  // ============================================================
  RVRNFinder.prototype._showResults = function () {
    var self = this;
    if (!this.results) return;
    this.results.removeAttribute('hidden');

    // Scroll to results
    var resultsTop = this.results.getBoundingClientRect().top + window.pageYOffset - 80;
    window.scrollTo({ top: resultsTop, behavior: 'smooth' });

    // Render first batch only — Load More reveals the rest progressively
    this.resultsData.rendered = 0;
    this._renderBatch();

    // Update count
    this.collectionCount.textContent = this.resultsData.totalCount + ' designs';

    // Render active filters
    this._renderActiveFilters();

    // Show Load More when there is anything left to reveal
    this._updateLoadMore();
  };

  // Render the next PAGE_BATCH products from the aggregated results
  RVRNFinder.prototype._renderBatch = function () {
    var self = this;
    var rd = this.resultsData;
    var start = rd.rendered || 0;
    var batch = rd.products.slice(start, start + PAGE_BATCH);
    if (batch.length) {
      self._renderGrid(batch, start > 0);
      rd.rendered = start + batch.length;
    }
  };

  // Load More button states:
  // - hidden: no results, or the whole revealed set is tiny (<= PAGE_BATCH)
  //   and fully rendered — e.g. 9 matches with a pending server page must not
  //   surface a misleading "Show More"
  // - "Show X More": cached products still unrevealed
  // - "Show More": cache exhausted but a server page is pending (large tier)
  // - "Show Less": everything revealed and worth collapsing (mirrors hero)
  RVRNFinder.prototype._updateLoadMore = function () {
    var rd = this.resultsData;
    var hasMoreCached = (rd.rendered || 0) < rd.products.length;
    var allRevealed = !hasMoreCached;
    if (this.loadMore) {
      var hideAll = !rd.products.length || (allRevealed && rd.products.length <= PAGE_BATCH);
      if (hideAll) {
        this.loadMore.style.display = 'none';
        return;
      }
      this.loadMore.style.display = '';
      if (this.loadMoreBtn) {
        if (allRevealed && !rd.hasNextPage) {
          this._loadMoreMode = 'collapse';
          this.loadMoreBtn.textContent = 'Show Less';
        } else if (allRevealed && rd.hasNextPage) {
          this._loadMoreMode = 'more';
          this.loadMoreBtn.textContent = 'Show More';
        } else {
          this._loadMoreMode = 'more';
          var remaining = rd.products.length - (rd.rendered || 0);
          this.loadMoreBtn.textContent = 'Show ' + Math.max(remaining, 0) + ' More';
        }
      }
    }
  };

  // Collapse the grid back to the first batch, then reveal again via Load More
  RVRNFinder.prototype._collapseResults = function () {
    var self = this;
    var rd = this.resultsData;
    var batch = rd.products.slice(0, PAGE_BATCH);
    rd.rendered = batch.length;
    self._renderGrid(batch, false);
    self._updateLoadMore();
    // The page height collapses with the grid; scroll back to the top of the
    // results so the shopper sees the remaining batch instead of blank space.
    if (self.results) {
      var top = self.results.getBoundingClientRect().top + window.pageYOffset - 80;
      window.scrollTo({ top: top, behavior: 'smooth' });
    }
  };

  RVRNFinder.prototype._renderGrid = function (products, append) {
    var self = this;
    var list = products || this.resultsData.products;
    var html = list.map(function (p) {
      return '<div class="rvrn-product-card">' + self._renderProductCard(p) + '</div>';
    }).join('');
    if (append && this.productGrid) {
      this.productGrid.insertAdjacentHTML('beforeend', html);
    } else if (this.productGrid) {
      this.productGrid.innerHTML = html;
    }

    // Lazy load images
    if (this.productGrid) {
      this.productGrid.querySelectorAll('.rvrn-product-card__image').forEach(function (img) {
        if (img.complete && img.naturalWidth > 0) img.classList.add('is-loaded');
      });
    }
  };

  RVRNFinder.prototype._renderActiveFilters = function () {
    var s = this.state;
    var chips = [];
    if (s.year && s.make && s.model) chips.push('Vehicle: ' + s.year + ' ' + s.make + ' ' + s.model + '<b class="rvrn-finder__filter-remove" data-action="removeVehicle">×</b>');
    if (s.style) chips.push('Style: ' + s.style + '<b class="rvrn-finder__filter-remove" data-action="removeStyle">×</b>');
    if (s.finish) chips.push('Finish: ' + s.finish + '<b class="rvrn-finder__filter-remove" data-action="removeFinish">×</b>');

    var html = '<span class="rvrn-finder__filter-label">Your selections</span>';
    if (chips.length === 0) {
      html += '<span class="rvrn-finder__filter-chip">None</span>';
    } else {
      html += chips.map(function (c) { return '<span class="rvrn-finder__filter-chip">' + c + '</span>'; }).join('');
      html += '<button class="rvrn-finder__clear-filters" data-action="clearAll">Start over</button>';
    }
    this.activeFilters.innerHTML = html;
  };

  // ============================================================
  // Event binding
  // ============================================================
  RVRNFinder.prototype._bindEvents = function () {
    var self = this;

    // Stage clicks (delegated)
    if (this.stage) { this.stage.addEventListener('click', function (e) {
      var target = e.target.closest('[data-action]');
      if (!target) return;
      var action = target.dataset.action;
      var value = target.dataset.value;
      var step = target.dataset.step;
      var mode = target.dataset.mode;

      switch (action) {
        case 'switchMode':
          self.state.step = 1;
          self.state.vehicleMode = mode || 'standard';
          self._renderStep();
          self._updateProgress();
          if (self.results) self.results.setAttribute('hidden', '');
          break;
        case 'continueVehicle':
          self._continueVehicle();
          break;
        case 'selectDirection':
          self.state.style = value;
          self.state.step = 3;
          self._renderStep();
          self._updateProgress();
          self._pushURL();
          if (self.results) self.results.setAttribute('hidden', '');
          // If finish already selected, auto-trigger search
          if (self.state.finish) self._triggerSearch();
          break;
        case 'selectFinish':
          self.state.finish = value;
          self._renderStep();
          self._updateResultBar();
          self._pushURL();
          // Auto-trigger search on finish selection
          self._triggerSearch();
          break;
        case 'showMoreFinishes':
          self.state.showMoreFinishes = true;
          self._renderStep();
          break;
        case 'showFewerFinishes':
          self.state.showMoreFinishes = false;
          self._renderStep();
          break;
        case 'backToStep':
          self.state.step = parseInt(step);
          self._renderStep();
          self._updateProgress();
          if (self.results) self.results.setAttribute('hidden', '');
          break;
        default: break;
      }
    });
    } // end if (this.stage)

    // Stage change events
    if (this.stage) this.stage.addEventListener('change', function (e) {
      var field = e.target.closest('[data-field]');
      if (!field) return;
      var name = field.dataset.field;
      if (name === 'year') { self.state.year = field.value; self.state.make = ''; self.state.model = ''; }
      if (name === 'make') { self.state.make = field.value; self.state.model = ''; }
      if (name === 'model') { self.state.model = field.value; }
      // Only standard select mode needs a re-render (cascading option lists).
      // Classic text inputs are synced live via input events; rebuilding here
      // would wipe the user's typed value.
      if ((name === 'year' || name === 'make') && self.state.vehicleMode === 'standard') self._renderStep();
    });

    // Stage input events (classic mode)
    if (this.stage) this.stage.addEventListener('input', function (e) {
      var field = e.target.closest('[data-field]');
      if (!field) return;
      var name = field.dataset.field;
      self.state[name] = field.value;
      if (name === 'year') { self.state.make = ''; self.state.model = ''; }
      if (name === 'make') self.state.model = '';
    });

    // Step progress clicks
    this.stepItems.forEach(function (btn, i) {
      btn.addEventListener('click', function () {
        self.state.step = i + 1;
        self._renderStep();
        self._updateProgress();
      });
    });

    // Active filters (delegated)
    if (this.activeFilters) this.activeFilters.addEventListener('click', function (e) {
      var filterTarget = e.target.closest('[data-action]');
      if (!filterTarget) return;
      switch (filterTarget.dataset.action) {
        case 'removeVehicle':
          self.state.year = ''; self.state.make = ''; self.state.model = ''; self.state.vehicleTag = null; self.state.filterLevel = '';
          self.state.step = 1;
          self._renderStep(); self._updateProgress(); self._pushURL();
          self.results.setAttribute('hidden', '');
          break;
        case 'removeStyle':
          self.state.style = '';
          self.state.step = 2;
          self._renderStep(); self._updateProgress(); self._pushURL();
          self.results.setAttribute('hidden', '');
          break;
        case 'removeFinish':
          self.state.finish = '';
          self.state.step = 3;
          self._renderStep(); self._updateResultBar(); self._pushURL();
          self.results.setAttribute('hidden', '');
          break;
        case 'clearAll':
          self.state.year = ''; self.state.make = ''; self.state.model = ''; self.state.style = ''; self.state.finish = '';
          self.state.vehicleTag = null; self.state.filterLevel = ''; self.state.step = 1;
          self._renderStep(); self._updateProgress(); self._pushURL();
          self.results.setAttribute('hidden', '');
          break;
      }
    });

    // Density toggle — results are outside root, use document listener
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('.rvrn-finder__grid-density-btn');
      if (!btn) return;
      if (btn.dataset.gridColumns) {
        self.productColumns = parseInt(btn.dataset.gridColumns);
        try { localStorage.setItem(GRID_STORAGE_KEY, String(self.productColumns)); } catch (e) {}
      }
      if (btn.dataset.gridColumnsMobile) {
        self.mobileProductColumns = parseInt(btn.dataset.gridColumnsMobile);
        try { localStorage.setItem(MOBILE_GRID_STORAGE_KEY, String(self.mobileProductColumns)); } catch (e) {}
      }
      self._applyDensity();
    });

    // Show My Options — trigger search + scroll to results
    if (this.showBtn) {
      this.showBtn.addEventListener('click', function (e) {
        e.preventDefault();
        self._triggerSearch();
      });
    }

    // Return to finder
    if (this.returnBtn) {
      this.returnBtn.addEventListener('click', function () {
        var finderTop = self.root.getBoundingClientRect().top + window.pageYOffset - 40;
        window.scrollTo({ top: finderTop, behavior: 'smooth' });
      });
    }

    // Load more / collapse — paginates the active tier or collapses the grid
    if (this.loadMoreBtn) {
      this.loadMoreBtn.addEventListener('click', function () {
        if (self._loadMoreMode === 'collapse') {
          self._collapseResults();
        } else {
          self._loadMore();
        }
      });
    }
  };

  // ============================================================
  // Continue vehicle → next step
  // ============================================================
  RVRNFinder.prototype._continueVehicle = function () {
    var s = this.state;
    if (s.vehicleMode === 'standard') {
      if (!s.year || !s.make || !s.model) return;
    } else {
      if (!s.year || !s.make || !s.model) return;
    }
    this._resolveVehicleTag();
    this.state.step = 2;
    this._pushURL();
    this._renderStep();
    this._updateProgress();
  };

  // ============================================================
  // Trigger search
  // ============================================================
  RVRNFinder.prototype._triggerSearch = function () {
    var self = this;
    this.resultsData = { products: [], degradation: null, totalCount: 0, currentLevelTags: [], currentPage: 1, hasNextPage: false };
    this._runDegradation().then(function () {
      self._showResults();
    }).catch(function (err) {
      console.error('RVRN Finder search failed:', err);
    });
  };

  RVRNFinder.prototype._loadMore = function () {
    var self = this;
    var rd = this.resultsData;

    // Cached results still have unrevealed products — reveal the next batch
    // locally with no network request.
    if ((rd.rendered || 0) < rd.products.length) {
      self._renderBatch();
      self._updateLoadMore();
      return;
    }

    // Cache exhausted but a server-side next page exists. Page forward
    // silently until available products are found or the collection is
    // exhausted (bounded, so an OOS-heavy tail cannot stall the UI). A click
    // that yields nothing hides the button — no dead "Show More".
    if (this._loadMoreInFlight) return;
    this._loadMoreInFlight = true;

    var pagesFetched = 0;
    var nextPage = (rd.currentPage || 1) + 1;

    function fetchNext() {
      if (pagesFetched >= MAX_COLLECT_PAGES) {
        // Scanned the bound without finding any available products — nothing
        // more to show from the shopper's perspective.
        rd.hasNextPage = false;
        self._loadMoreInFlight = false;
        self._updateLoadMore();
        return;
      }
      pagesFetched++;
      return self._executeSearch(rd.currentLevelTags || [], nextPage).then(function (result) {
        var seen = new Set(rd.products.map(function (p) { return p.id; }));
        var added = [];
        result.products.forEach(function (p) {
          if (!seen.has(p.id)) {
            seen.add(p.id);
            p._level = rd.degradation || 0;
            rd.products.push(p);
            added.push(p);
          }
        });
        rd.currentPage = nextPage;
        rd.hasNextPage = result.hasNextPage;
        if (added.length) {
          self._loadMoreInFlight = false;
          rd.totalCount = rd.products.length;
          self._renderBatch();
          if (self.collectionCount) self.collectionCount.textContent = rd.totalCount + ' designs';
          self._updateLoadMore();
          return;
        }
        if (result.hasNextPage) {
          nextPage++;
          return fetchNext();
        }
        self._loadMoreInFlight = false;
        self._updateLoadMore();
      }).catch(function (err) {
        self._loadMoreInFlight = false;
        console.error('RVRN Finder load more failed:', err);
      });
    }

    fetchNext();
  };

  // ============================================================
  // Progress update
  // ============================================================
  RVRNFinder.prototype._updateProgress = function () {
    var self = this;
    this.stepItems.forEach(function (btn, i) {
      var stepNum = i + 1;
      btn.classList.remove('active', 'done');
      if (stepNum === self.state.step) btn.classList.add('active');
      if (stepNum < self.state.step) btn.classList.add('done');
    });
    this.stepTitle.textContent = this.config['step' + this.state.step + 'Title'];
  };

  // ============================================================
  // Density toggle
  // ============================================================
  RVRNFinder.prototype._applyDensity = function () {
    this.root.dataset.productColumns = String(this.productColumns);
    document.documentElement.style.setProperty('--product-columns', String(this.productColumns));
    document.documentElement.style.setProperty('--product-columns-mobile', String(this.mobileProductColumns));

    // Update button states — search globally (buttons are outside Finder root)
    var btns = document.querySelectorAll('.rvrn-finder__grid-density-btn');
    btns.forEach(function (b) {
      if (b.dataset.gridColumns) {
        b.setAttribute('aria-pressed', parseInt(b.dataset.gridColumns) === this.productColumns);
      }
      if (b.dataset.gridColumnsMobile) {
        b.setAttribute('aria-pressed', parseInt(b.dataset.gridColumnsMobile) === this.mobileProductColumns);
      }
    }.bind(this));
  };

  // ============================================================
  // Utilities
  // ============================================================
  RVRNFinder.prototype._selectField = function (label, options, currentValue, name) {
    var html = '<div class="rvrn-finder__field"><select data-field="' + this._escapeAttr(name) + '">';
    html += '<option value="">' + label + '</option>';
    options.forEach(function (opt) {
      html += '<option value="' + this._escapeAttr(opt) + '"' + (String(opt) === String(currentValue) ? ' selected' : '') + '>' + this._escapeHtml(String(opt)) + '</option>';
    }, this);
    html += '</select></div>';
    return html;
  };

  RVRNFinder.prototype._escapeHtml = function (str) {
    if (!str) return '';
    return String(str).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  };

  RVRNFinder.prototype._escapeAttr = function (str) {
    if (!str) return '';
    return String(str).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  };

 })();

